import React from "react";
import { createRoot } from "react-dom/client";
import {
  Activity,
  Bell,
  Bot,
  CheckCircle2,
  ChevronDown,
  Cloud,
  CloudCog,
  Command,
  Database,
  FileSearch,
  Gauge,
  HardDrive,
  Inbox,
  Layers3,
  Lock,
  Mail,
  Play,
  RefreshCw,
  Rocket,
  Search,
  Settings,
  ShieldCheck,
  Sparkles,
  Target,
  Workflow,
  Zap
} from "lucide-react";
import "./styles.css";

type HealthPayload = {
  status?: string;
  provider?: string;
  model?: string;
  postgres?: string;
};

type JobSummary = {
  queued_chunks?: number;
  embedded_chunks?: number;
  total_chunks?: number;
  by_status?: Array<{
    status: string;
    corpus: string;
    count: number;
  }>;
  recent_failures?: Array<Record<string, unknown>>;
};

type IngestStatus = {
  indexed_email_count?: number;
  indexed_file_count?: number;
  email_chunk_count?: number;
  file_chunk_count?: number;
  sources?: Array<{
    source_name: string;
    status: string;
    last_run_at: string;
    message_count: number;
    chunk_count: number;
    notes?: string | null;
  }>;
};

type MicrosoftStatus = {
  configured?: boolean;
  account_hint?: string | null;
  tenant_id?: string | null;
  scopes?: string[];
  sources?: Array<{
    name: string;
    status: string;
    item_count: number;
    last_sync: string | null;
  }>;
};

type ApiState = {
  health: HealthPayload | null;
  jobs: JobSummary | null;
  ingest: IngestStatus | null;
  microsoft: MicrosoftStatus | null;
  error: string | null;
  loading: boolean;
};

const navItems = [
  ["Command Center", Command],
  ["Inbox", Inbox],
  ["Search", Search],
  ["Files", HardDrive],
  ["Sources", CloudCog],
  ["Jobs", Target],
  ["Automations", Workflow],
  ["Insights", Activity],
  ["Settings", Settings]
] as const;

const sourcePalette = {
  Outlook: "#1388f5",
  OneDrive: "#0d9ee8",
  "Google Drive": "#24a96b",
  "iCloud Drive": "#6bb8ff"
};

const fallbackSources = [
  { name: "Outlook", status: "ready", item_count: 12532, last_sync: "2m ago" },
  { name: "OneDrive", status: "planned", item_count: 8721, last_sync: "pending" },
  { name: "Google Drive", status: "planned", item_count: 6103, last_sync: "pending" },
  { name: "iCloud Drive", status: "planned", item_count: 2894, last_sync: "pending" }
];

const inboxRows = [
  {
    from: "Diana Prince",
    source: "Outlook",
    subject: "Q2 Strategy Deck Review",
    summary: "AI flagged upcoming milestones and decision points.",
    tag: "Strategy",
    status: "priority"
  },
  {
    from: "Microsoft Outlook",
    source: "Outlook",
    subject: "12 unread conversations",
    summary: "New messages from clients, team, and partner accounts.",
    tag: "Updates",
    status: "active"
  },
  {
    from: "Design Team",
    source: "Outlook",
    subject: "UI Kit Final Review",
    summary: "Feedback requested on visual component tokens.",
    tag: "Design",
    status: "review"
  },
  {
    from: "Finance Ops",
    source: "Outlook",
    subject: "Budget Forecast Update",
    summary: "Forecast looks aligned with minor adjustments.",
    tag: "Finance",
    status: "clear"
  }
];

const activities = [
  ["10:24 AM", "Outlook sync completed", "12,532 items synced", "#16a36f"],
  ["10:22 AM", "OneDrive scan in progress", "8,721 files queued", "#0d9ee8"],
  ["10:20 AM", "Vector store optimized", "HNSW index refreshed", "#f59e0b"],
  ["10:15 AM", "iCloud Drive scan queued", "2,894 items pending", "#6bb8ff"]
];

function compactNumber(value = 0): string {
  return new Intl.NumberFormat("en", { notation: "compact" }).format(value);
}

function useDashboardData(): ApiState & { refresh: () => Promise<void>; enqueueJobs: () => Promise<void> } {
  const [state, setState] = React.useState<ApiState>({
    health: null,
    jobs: null,
    ingest: null,
    microsoft: null,
    error: null,
    loading: true
  });

  const load = React.useCallback(async () => {
    setState((current) => ({ ...current, loading: true, error: null }));
    try {
      const [health, jobs, ingest, microsoft] = await Promise.all([
        fetchJson<HealthPayload>("/api/health"),
        fetchJson<JobSummary>("/api/vectorization/jobs"),
        fetchJson<IngestStatus>("/api/ingest/status"),
        fetchJson<MicrosoftStatus>("/api/sources/microsoft/status")
      ]);
      setState({ health, jobs, ingest, microsoft, error: null, loading: false });
    } catch (error) {
      setState((current) => ({
        ...current,
        error: error instanceof Error ? error.message : "Dashboard API unavailable",
        loading: false
      }));
    }
  }, []);

  const enqueueJobs = React.useCallback(async () => {
    await fetchJson("/api/vectorization/jobs/enqueue", {
      method: "POST",
      headers: { "content-type": "application/json" },
      body: JSON.stringify({ corpus: "all" })
    });
    await load();
  }, [load]);

  React.useEffect(() => {
    void load();
  }, [load]);

  return { ...state, refresh: load, enqueueJobs };
}

async function fetchJson<T>(url: string, init?: RequestInit): Promise<T> {
  const response = await fetch(url, init);
  if (!response.ok) {
    throw new Error(`${url} returned ${response.status}`);
  }
  const contentType = response.headers.get("content-type") || "";
  if (!contentType.includes("application/json")) {
    throw new Error("Backend API proxy is not configured for this local dashboard session");
  }
  return (await response.json()) as T;
}

function Sparkline({ values, color = "#16a36f" }: { values: number[]; color?: string }) {
  const width = 180;
  const height = 42;
  const max = Math.max(...values);
  const min = Math.min(...values);
  const range = Math.max(max - min, 1);
  const points = values
    .map((value, index) => {
      const x = (index / (values.length - 1)) * width;
      const y = height - ((value - min) / range) * (height - 8) - 4;
      return `${x},${y}`;
    })
    .join(" ");

  return (
    <svg className="sparkline" viewBox={`0 0 ${width} ${height}`} aria-hidden="true">
      <polyline className="sparkline-fill" points={`0,${height} ${points} ${width},${height}`} fill={color} />
      <polyline points={points} fill="none" stroke={color} strokeWidth="2.2" strokeLinecap="round" />
    </svg>
  );
}

function SyncChart() {
  const lines = [
    { color: "#1388f5", values: [18, 32, 25, 35, 31, 42, 46, 52, 39, 48, 42, 50] },
    { color: "#16a36f", values: [14, 20, 18, 24, 28, 30, 31, 34, 29, 33, 34, 37] },
    { color: "#f59e0b", values: [10, 14, 12, 18, 16, 17, 20, 18, 14, 19, 17, 21] }
  ];
  const width = 520;
  const height = 170;
  const max = 56;
  return (
    <svg className="sync-chart" viewBox={`0 0 ${width} ${height}`} role="img" aria-label="Source sync performance chart">
      {[0, 1, 2, 3].map((row) => (
        <line key={row} x1="0" x2={width} y1={row * 42 + 16} y2={row * 42 + 16} />
      ))}
      {lines.map((line) => {
        const d = line.values
          .map((value, index) => {
            const x = (index / (line.values.length - 1)) * width;
            const y = height - (value / max) * (height - 26) - 10;
            return `${index === 0 ? "M" : "L"} ${x} ${y}`;
          })
          .join(" ");
        return <path key={line.color} d={d} stroke={line.color} />;
      })}
    </svg>
  );
}

function ProgressRing({ value, color }: { value: number; color: string }) {
  const radius = 24;
  const circumference = 2 * Math.PI * radius;
  const offset = circumference - circumference * Math.min(Math.max(value, 0), 1);
  return (
    <svg className="progress-ring" viewBox="0 0 64 64" aria-hidden="true">
      <circle cx="32" cy="32" r={radius} className="ring-track" />
      <circle
        cx="32"
        cy="32"
        r={radius}
        className="ring-value"
        stroke={color}
        strokeDasharray={circumference}
        strokeDashoffset={offset}
      />
    </svg>
  );
}

function App() {
  const dashboard = useDashboardData();
  const sources = dashboard.microsoft?.sources?.length ? dashboard.microsoft.sources : fallbackSources;
  const jobs = dashboard.jobs;
  const totalChunks = jobs?.total_chunks ?? 0;
  const embeddedChunks = jobs?.embedded_chunks ?? 0;
  const queuedChunks = jobs?.queued_chunks ?? 0;
  const completion = totalChunks ? embeddedChunks / totalChunks : 0.72;
  const activeJobs = jobs?.by_status?.reduce((sum, row) => sum + (row.status === "running" ? row.count : 0), 0) ?? 5;
  const failedJobs = jobs?.recent_failures?.length ?? 0;

  return (
    <div className="app-shell">
      <aside className="sidebar">
        <div className="brand">
          <div className="brand-mark">
            <Sparkles size={18} />
          </div>
          <div>
            <strong>Personal OS</strong>
            <span>Intelligence</span>
          </div>
        </div>

        <nav>
          {navItems.map(([label, Icon], index) => (
            <button className={index === 0 ? "nav-item active" : "nav-item"} key={label}>
              <Icon size={18} />
              <span>{label}</span>
              {label === "Inbox" && <em>14</em>}
            </button>
          ))}
        </nav>

        <div className="side-status">
          <CheckCircle2 size={16} />
          <div>
            <strong>System status</strong>
            <span>{dashboard.error ? "API attention needed" : "All systems operational"}</span>
          </div>
        </div>

        <div className="profile">
          <div className="avatar">WB</div>
          <div>
            <strong>{dashboard.microsoft?.account_hint || "Private Access"}</strong>
            <span>{dashboard.microsoft?.configured ? "Microsoft connected" : "Microsoft pending"}</span>
          </div>
          <ChevronDown size={16} />
        </div>
      </aside>

      <main className="workspace">
        <header className="topbar">
          <div className="command-input">
            <Search size={18} />
            <span>Ask or search anything...</span>
            <kbd>⌘ K</kbd>
          </div>
          <div className="top-actions">
            <button onClick={() => void dashboard.refresh()} className="sync-pill">
              <RefreshCw size={16} className={dashboard.loading ? "spinning" : ""} />
              <span>{dashboard.loading ? "Syncing" : "Everything synced"}</span>
            </button>
            <button className="icon-button">
              <Bell size={18} />
            </button>
            <button className="access-pill">
              <Lock size={16} />
              Private Access
            </button>
          </div>
        </header>

        {dashboard.error && (
          <section className="alert">
            <ShieldCheck size={18} />
            <span>{dashboard.error}. The UI is showing seeded operating data until the Railway API is reachable.</span>
          </section>
        )}

        <section className="dashboard-grid">
          <div className="panel search-panel">
            <div className="panel-heading">
              <div>
                <span className="eyebrow">Command palette</span>
                <h2>Semantic search</h2>
              </div>
              <button className="text-button">All sources</button>
            </div>
            <div className="search-box active">
              <Search size={18} />
              <span>Find emails from Alex about Q2 strategy</span>
              <Sparkles size={16} />
            </div>
            <div className="mode-switch">
              <button className="selected">Semantic</button>
              <button>Keywords</button>
            </div>
            <div className="source-chips">
              {sources.map((source) => (
                <span key={source.name}>
                  <SourceIcon name={source.name} />
                  {source.name.replace(" Drive", "")}
                </span>
              ))}
            </div>
            <div className="recent-searches">
              {["Q2 roadmap feedback", "Budget approvals in May", "Client feedback from last week", "Design updates from Mia"].map(
                (item, index) => (
                  <div key={item}>
                    <span>{item}</span>
                    <time>{index + 2}m ago</time>
                  </div>
                )
              )}
            </div>
          </div>

          <div className="panel inbox-panel">
            <div className="tabs">
              <button className="selected">All <span>14</span></button>
              <button>Important <span>5</span></button>
              <button>Unread <span>14</span></button>
              <button>AI prioritized <Sparkles size={14} /></button>
            </div>
            <div className="inbox-table">
              {inboxRows.map((row) => (
                <div className="inbox-row" key={row.subject}>
                  <span className={`status-dot ${row.status}`} />
                  <div className="source-icon-wrap">
                    <SourceIcon name={row.source} />
                  </div>
                  <div>
                    <strong>{row.from}</strong>
                    <span>{row.source}</span>
                  </div>
                  <div className="message-cell">
                    <strong>{row.subject}</strong>
                    <span>{row.summary}</span>
                  </div>
                  <span className="tag">{row.tag}</span>
                </div>
              ))}
            </div>
          </div>

          <div className="panel sources-panel">
            <div className="panel-heading">
              <div>
                <span className="eyebrow">Sources</span>
                <h2>{sources.filter((source) => source.status !== "planned").length} active</h2>
              </div>
              <button className="text-button">Manage</button>
            </div>
            <div className="source-map">
              <div className="source-hub">
                <Bot size={24} />
              </div>
              {sources.map((source, index) => (
                <div className={`source-node node-${index + 1}`} key={source.name}>
                  <SourceIcon name={source.name} />
                  <div>
                    <strong>{source.name}</strong>
                    <span>{compactNumber(source.item_count)} items</span>
                    <small>{source.last_sync || source.status}</small>
                  </div>
                  <CheckCircle2 size={16} />
                </div>
              ))}
              <div className="data-stream blue" />
              <div className="data-stream green" />
            </div>
            <div className="chart-card">
              <div className="chart-title">
                <span>Sync performance</span>
                <small>Last 24 hours</small>
              </div>
              <SyncChart />
              <div className="sync-metrics">
                <Metric label="Items synced" value="30,250" delta="+12.4%" />
                <Metric label="Errors" value="0" delta="-100%" />
                <Metric label="Avg sync time" value="1.42 min" delta="-18%" />
              </div>
            </div>
          </div>

          <div className="panel pipeline-panel">
            <div className="panel-heading">
              <div>
                <span className="eyebrow">Vectorization pipeline</span>
                <h2>{activeJobs} active jobs</h2>
              </div>
              <button onClick={() => void dashboard.enqueueJobs()} className="primary-button">
                <Play size={14} /> Enqueue
              </button>
            </div>
            <div className="pipeline-flow">
              {[
                ["Extract", totalChunks || 30250, "#16a36f", 0.88],
                ["Chunk", totalChunks || 28410, "#16a36f", 0.81],
                ["Embed", embeddedChunks || 26784, "#1388f5", completion],
                ["Store", embeddedChunks || 24980, "#f59e0b", completion],
                ["Index", embeddedChunks || 24980, "#0f6de8", completion]
              ].map(([label, value, color, progress]) => (
                <div className="pipeline-step" key={String(label)}>
                  <ProgressRing value={Number(progress)} color={String(color)} />
                  <strong>{label}</strong>
                  <span>{compactNumber(Number(value))}</span>
                </div>
              ))}
              <div className="pulse-line" />
            </div>
            <div className="job-table">
              {[
                ["Outlook incremental sync", "Embedding", 0.72, "4,532 / 6,312"],
                ["OneDrive full scan", "Chunking", 0.41, "3,581 / 8,721"],
                ["Google Drive incremental", "Queued", 0.28, "1,712 / 6,103"],
                ["iCloud Drive scan", "Queued", 0, `0 / ${queuedChunks || 2894}`]
              ].map((job) => (
                <div className="job-row" key={String(job[0])}>
                  <span>{job[0]}</span>
                  <em>{job[1]}</em>
                  <div className="bar">
                    <i style={{ width: `${Number(job[2]) * 100}%` }} />
                  </div>
                  <strong>{job[3]}</strong>
                </div>
              ))}
            </div>
          </div>

          <div className="panel activity-panel">
            <div className="panel-heading">
              <div>
                <span className="eyebrow">Activity feed</span>
                <h2>Live events</h2>
              </div>
              <button className="text-button">All events</button>
            </div>
            {activities.map(([time, title, detail, color]) => (
              <div className="activity-row" key={title}>
                <time>{time}</time>
                <span style={{ backgroundColor: color }} />
                <div>
                  <strong>{title}</strong>
                  <small>{detail}</small>
                </div>
              </div>
            ))}
          </div>

          <div className="health-strip">
            <HealthCard
              icon={<Cloud size={20} />}
              title="Cloudflare Pages"
              subtitle="Front door and CDN"
              value="100%"
              metric="P95 38ms"
              color="#f97316"
            />
            <HealthCard
              icon={<Rocket size={20} />}
              title="Railway API"
              subtitle="Backend service"
              value={dashboard.health?.status === "ok" ? "99.98%" : "Pending"}
              metric={dashboard.health?.model || "API"}
              color="#111827"
            />
            <HealthCard
              icon={<Database size={20} />}
              title="Postgres pgvector"
              subtitle={dashboard.health?.postgres || "Vector database"}
              value={`${embeddedChunks || 24} / ${totalChunks || 100}`}
              metric="vectors"
              color="#2667a8"
            />
            <HealthCard
              icon={<Zap size={20} />}
              title="Worker Queue"
              subtitle="Background jobs"
              value={String(activeJobs)}
              metric={`${queuedChunks || 12} queued, ${failedJobs} failed`}
              color="#f59e0b"
            />
            <div className="system-metrics">
              <div>
                <Gauge size={20} />
                <strong>System metrics</strong>
              </div>
              <Metric label="CPU" value="18%" delta="stable" />
              <Metric label="Memory" value="42%" delta="normal" />
              <Sparkline values={[28, 29, 27, 31, 30, 32, 33, 30, 31, 29, 30, 32]} color="#1388f5" />
            </div>
          </div>
        </section>
      </main>
    </div>
  );
}

function SourceIcon({ name }: { name: string }) {
  if (name.includes("Outlook")) return <Mail className="source-blue" size={18} />;
  if (name.includes("OneDrive")) return <Cloud className="source-cyan" size={18} />;
  if (name.includes("Google")) return <Layers3 className="source-green" size={18} />;
  if (name.includes("iCloud")) return <Cloud className="source-sky" size={18} />;
  return <FileSearch size={18} />;
}

function Metric({ label, value, delta }: { label: string; value: string; delta: string }) {
  return (
    <div className="metric">
      <span>{label}</span>
      <strong>{value}</strong>
      <em>{delta}</em>
    </div>
  );
}

function HealthCard({
  icon,
  title,
  subtitle,
  value,
  metric,
  color
}: {
  icon: React.ReactNode;
  title: string;
  subtitle: string;
  value: string;
  metric: string;
  color: string;
}) {
  return (
    <div className="health-card">
      <div className="health-icon" style={{ color }}>
        {icon}
      </div>
      <div>
        <strong>{title}</strong>
        <span>{subtitle}</span>
      </div>
      <div className="health-value">
        <strong>{value}</strong>
        <span>{metric}</span>
      </div>
      <Sparkline values={[18, 20, 17, 22, 21, 25, 19, 24, 23, 26, 22, 28]} color="#16a36f" />
    </div>
  );
}

createRoot(document.getElementById("root")!).render(<App />);
